import { gql } from "@apollo/client";
import { alpha, Button, ButtonBase, Paper } from "@mui/material";
import videoPlaceholderImage from "assets/video_placeholder.jpg";
import DateTime from "atoms/DateTime";
import VideoThumbnail from "atoms/VideoThumbnail";
import VideoThumbnailStatusLabel from "atoms/VideoThumbnailStatusLabel";
import { COLOR_BLACK, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK, FONT_FAMILY_KNOCKOUT_JUNIOR } from "helpers/FONT";
import { SPACING, SPACING_HALF } from "helpers/spacings";
import { CloudUploadOutline, TimerSand } from "mdi-material-ui";
import React from "react";

export const GameEntryFragment = gql`
  fragment GameEntryFragment on Game {
    id
    thumbnailUrl
    videoProcessing
    gameVideoStatus
    videoArchived
    homeScore
    awayScore
    playedAt
    league {
      id
      name
    }
    home {
      id
      name
    }
    away {
      id
      name
    }
    livePotwCompetition {
      id
    }
    statsVisible
    eventThumbnailUrl
    org {
      id
      name
    }
    gamePlayers {
      id
      person {
        id
        fullNameWithAnonymisation
      }
    }
  }
`;

const VIDEO_STATUS_CONFIG = {
  unavailable: {
    getLabel: () => <VideoThumbnailStatusLabel message="Video is unavailable" />,
    getThumbnail: () => videoPlaceholderImage,
    getBottomRightLabel: () => null,
  },
  archived: {
    getLabel: () => null,
    getThumbnail: (game) => game?.eventThumbnailUrl || videoPlaceholderImage,
    getBottomRightLabel: () => "Highlights",
  },
  uploading: {
    getLabel: () => (
      <VideoThumbnailStatusLabel
        message="Video is still uploading, please check back later"
        icon={CloudUploadOutline}
      />
    ),
    getThumbnail: () => videoPlaceholderImage,
    getBottomRightLabel: () => null,
  },
  encoding: {
    getLabel: () => (
      <VideoThumbnailStatusLabel message="Video is still encoding, please check back later" icon={TimerSand} />
    ),
    getThumbnail: () => videoPlaceholderImage,
    getBottomRightLabel: () => null,
  },
  available: {
    getLabel: () => null,
    getThumbnail: (game) => game?.thumbnailUrl || videoPlaceholderImage,
    getBottomRightLabel: (game) => (game.livePotwCompetition ? "Plays of the Week" : null),
  },
  default: {
    getLabel: () => null,
    getThumbnail: () => videoPlaceholderImage,
    getBottomRightLabel: () => null,
  },
};

function getGameVideoPresentation(game) {
  const status = game?.gameVideoStatus?.toLowerCase();
  const config = (status && VIDEO_STATUS_CONFIG[status]) || VIDEO_STATUS_CONFIG.default;

  return {
    status,
    videoStatusLabel: config.getLabel?.(game) ?? null,
    thumbnailUrl: config.getThumbnail?.(game) ?? videoPlaceholderImage,
    bottomRightLabel: config.getBottomRightLabel?.(game) ?? null,
  };
}

export default function GameEntry({ game }) {
  const { status: gameVideoStatus, videoStatusLabel, thumbnailUrl, bottomRightLabel } = getGameVideoPresentation(game);

  const buildBottomLabel = (text, style = {}, key = text) => (
    <div
      key={key}
      style={{
        display: "flex",
        flexFlow: "row nowrap",
        alignItems: "center",
        gap: "0.4em",
        fontSize: 12,
        fontFamily: FONT_FAMILY_GRAPHIK,
        color: COLOR_WHITE_LIGHTER,
        backgroundColor: alpha(COLOR_BLACK, 0.8),
        borderRadius: 4,
        padding: "4px 8px",
        textTransform: "none",
        ...style,
      }}
    >
      {text}
    </div>
  );

  // by pass the game overview page if stats are not visible e.g pickleball games
  const viewGameLink = game.statsVisible ? `/game/${game.id}/overview` : `/game/${game.id}/video`;
  const bottomRightBadge = bottomRightLabel ? buildBottomLabel(bottomRightLabel, {}, "bottom-label") : null;
  const homeWon = game.homeScore > game.awayScore;
  const awayWon = game.awayScore > game.homeScore;
  const homeTeamScoreColor = homeWon ? COLOR_BLACK : alpha(COLOR_BLACK, 0.7);
  const awayTeamScoreColor = awayWon ? COLOR_BLACK : alpha(COLOR_BLACK, 0.7);

  return (
    <Paper
      key={game.id}
      style={{
        display: "flex",
        flexFlow: "column nowrap",
        width: "100%",
        minWidth: 0,
        justifyContent: "stretch",
        overflow: "hidden",
        borderRadius: 12,
      }}
    >
      <VideoThumbnail
        component={ButtonBase}
        style={{ flex: "1 1 auto", width: "100%" }}
        thumbnailUrl={thumbnailUrl}
        processing={game.videoProcessing}
        showPlayButton={gameVideoStatus === "available"}
        videoStatusLabel={videoStatusLabel}
        href={`/game/${game.id}/video`}
        rightLabel={bottomRightBadge}
        focusRipple
      />
      <div
        style={{
          flex: "1 1 auto",
          display: "flex",
          flexFlow: "column nowrap",
        }}
      >
        <div
          style={{
            fontFamily: FONT_FAMILY_KNOCKOUT_JUNIOR,
            textTransform: "uppercase",
            fontSize: "0.9em",
            lineHeight: 1,
            padding: `${SPACING_HALF} ${SPACING}`,
            color: alpha(COLOR_BLACK, 0.5),
          }}
        >
          {game.league.name}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            fontSize: 40,
            fontFamily: FONT_FAMILY_KNOCKOUT_JUNIOR,
            fontWeight: 400,
            opacity: game.statsVisible ? 1 : 0,
          }}
        >
          <div style={{ color: homeTeamScoreColor }}>{game.homeScore}</div>
          <div style={{ fontSize: ".5em", opacity: 0.5, padding: SPACING_HALF }}>&#8209;</div>
          <div style={{ color: awayTeamScoreColor }}>{game.awayScore}</div>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            fontSize: "0.75em",
            fontFamily: FONT_FAMILY_GRAPHIK,
          }}
        >
          <div
            style={{
              flex: "0 0 50%",
              maxWidth: "50%",
              textAlign: "right",
              paddingRight: SPACING_HALF,
              paddingLeft: SPACING_HALF,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              color: homeTeamScoreColor,
              fontWeight: homeWon ? 500 : 400,
            }}
          >
            {game.home.name}
          </div>
          <div
            style={{
              flex: "0 0 50%",
              maxWidth: "50%",
              textAlign: "left",
              paddingRight: SPACING_HALF,
              paddingLeft: SPACING_HALF,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              borderLeft: `1px solid ${alpha(COLOR_BLACK, 0.5)}`,
              color: awayTeamScoreColor,
              fontWeight: awayWon ? 500 : 400,
            }}
          >
            {game.away.name}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            opacity: 0.6,
            marginLeft: SPACING,
            fontFamily: FONT_FAMILY_GRAPHIK,
            paddingTop: SPACING_HALF,
            fontSize: "0.75em",
          }}
        >
          <DateTime value={game.playedAt} variant="time_weekday_date" />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "stretch",
          alignItems: "center",
          flex: "1 1 auto",
        }}
      >
        <Button
          style={{
            flex: "1 1 auto",
            backgroundColor: COLOR_BLACK,
            color: COLOR_WHITE_LIGHTER,
            margin: SPACING,
            fontFamily: FONT_FAMILY_GRAPHIK,
            borderRadius: 12,
          }}
          color="inherit"
          href={viewGameLink}
        >
          {"Overview"}
        </Button>
      </div>
    </Paper>
  );
}
